import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentEmailTemplates = () => {
  const [copiedId, setCopiedId] = useState(null);

  const recentEmails = [
    {
      id: 1,
      clientName: "Sarah Johnson",
      propertyAddress: "123 Main Street, Austin, TX",
      generatedDate: "2025-01-15T10:45:00Z",
      templateType: "Purchase Congratulations",
      subject: "Congratulations on Your New Home Purchase - Important Dates Inside",
      preview: `Dear Sarah,\n\nCongratulations on your successful offer for 123 Main Street! I'm excited to guide you through the next steps of your home buying journey.\n\nHere are your important contract dates:\n• Inspection Deadline: January 22, 2025\n• Appraisal Deadline: January 25, 2025\n• Financing Deadline: February 1, 2025\n• Closing Date: February 15, 2025\n\nI've created calendar invites for each deadline to help you stay organized...`,
      calendarLinks: {
        google: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Contract+Deadlines",
        outlook: "https://outlook.live.com/calendar/0/deeplink/compose",
        ics: "contract_deadlines_sarah_johnson.ics"
      }
    },
    {
      id: 2,
      clientName: "Michael Rodriguez",
      propertyAddress: "456 Oak Avenue, Dallas, TX",
      generatedDate: "2025-01-15T09:30:00Z",
      templateType: "Commercial Purchase",
      subject: "Commercial Property Purchase - Timeline & Next Steps",
      preview: `Dear Michael,\n\nThank you for choosing me to represent you in the purchase of 456 Oak Avenue. This commercial property acquisition has several critical milestones ahead.\n\nKey Contract Dates:\n• Due Diligence Period: January 30, 2025\n• Financing Contingency: February 5, 2025\n• Environmental Inspection: February 8, 2025\n• Closing Date: February 28, 2025\n\nI've prepared calendar invitations for each deadline...`,
      calendarLinks: {
        google: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Commercial+Contract+Deadlines",
        outlook: "https://outlook.live.com/calendar/0/deeplink/compose",
        ics: "commercial_deadlines_michael_rodriguez.ics"
      }
    },
    {
      id: 3,
      clientName: "David Thompson",
      propertyAddress: "321 Elm Street, San Antonio, TX",
      generatedDate: "2025-01-14T16:20:00Z",
      templateType: "Purchase Congratulations",
      subject: "Your Dream Home Awaits - Contract Timeline & Important Dates",
      preview: `Dear David,\n\nCongratulations! Your offer on 321 Elm Street has been accepted. I'm thrilled to help you navigate this exciting journey to homeownership.\n\nImportant Contract Milestones:\n• Home Inspection: January 21, 2025\n• Appraisal Completion: January 24, 2025\n• Loan Approval Deadline: January 31, 2025\n• Final Walkthrough: February 12, 2025\n• Closing Day: February 14, 2025\n\nTo keep you organized, I've created calendar events...`,
      calendarLinks: {
        google: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Home+Purchase+Timeline",
        outlook: "https://outlook.live.com/calendar/0/deeplink/compose",
        ics: "home_purchase_david_thompson.ics"
      }
    }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCopyToClipboard = async (emailContent, emailId) => {
    try {
      await navigator.clipboard.writeText(emailContent);
      setCopiedId(emailId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy email content:', err);
    }
  };

  const handleCalendarLink = (type, links) => {
    switch (type) {
      case 'google':
        window.open(links.google, '_blank');
        break;
      case 'outlook':
        window.open(links.outlook, '_blank');
        break;
      case 'ics':
        // Mock ICS file download
        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/calendar;charset=utf-8,' + encodeURIComponent('BEGIN:VCALENDAR\nVERSION:2.0\nEND:VCALENDAR'));
        element.setAttribute('download', links.ics);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        break;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-card">
      <div className="p-4 sm:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-heading font-semibold text-foreground">
            Recent Email Templates
          </h3>
          <Button
            variant="ghost"
            size="sm"
            iconName="ExternalLink"
            iconPosition="right"
            iconSize={14}
            onClick={() => window.location.href = '/email-template-generator'}
          >
            View All
          </Button>
        </div>
      </div>

      <div className="divide-y divide-border">
        {recentEmails.map((email) => (
          <div key={email.id} className="p-4 sm:p-6 hover:bg-muted/30 transition-colors">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Mail" size={16} color="var(--color-primary)" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-body font-medium text-foreground">
                      {email.clientName}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {email.propertyAddress}
                    </div>
                  </div>
                </div>
                
                <div className="mb-3">
                  <div className="text-sm font-body font-medium text-foreground mb-1 line-clamp-2">
                    Subject: {email.subject}
                  </div>
                  <div className="inline-flex items-center px-2 py-1 bg-secondary/10 border border-secondary/20 rounded-md text-xs font-body font-medium text-secondary">
                    {email.templateType}
                  </div>
                </div>

                <div className="bg-muted/50 rounded-lg p-3 sm:p-4 mb-4">
                  <div className="text-sm text-muted-foreground font-mono leading-relaxed whitespace-pre-line break-words">
                    {email.preview.length > 200 ? `${email.preview.substring(0, 200)}...` : email.preview}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-xs text-muted-foreground">
                Generated {formatDate(email.generatedDate)}
              </div>
              
              <div className="flex items-center justify-between sm:justify-end gap-4">
                {/* Calendar Links */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleCalendarLink('google', email.calendarLinks)}
                    className="p-2 rounded-md hover:bg-muted transition-colors"
                    title="Add to Google Calendar"
                  >
                    <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
                  </button>
                  <button
                    onClick={() => handleCalendarLink('outlook', email.calendarLinks)}
                    className="p-2 rounded-md hover:bg-muted transition-colors"
                    title="Add to Outlook Calendar"
                  >
                    <Icon name="CalendarDays" size={16} color="var(--color-muted-foreground)" />
                  </button>
                  <button
                    onClick={() => handleCalendarLink('ics', email.calendarLinks)}
                    className="p-2 rounded-md hover:bg-muted transition-colors"
                    title="Download ICS File"
                  >
                    <Icon name="Download" size={16} color="var(--color-muted-foreground)" />
                  </button>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  iconName={copiedId === email.id ? "Check" : "Copy"}
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => handleCopyToClipboard(email.preview, email.id)}
                  className={`${copiedId === email.id ? "text-success border-success/20" : ""} whitespace-nowrap`}
                >
                  {copiedId === email.id ? "Copied!" : "Copy"}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {recentEmails.length === 0 && (
        <div className="p-8 sm:p-12 text-center">
          <Icon name="Mail" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <div className="text-lg font-body font-medium text-muted-foreground mb-2">
            No email templates yet
          </div>
          <div className="text-sm text-muted-foreground">
            Process your first contract to generate client emails
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentEmailTemplates;